import { Button, Center, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useVerifyEmail } from '@/api/generated/registration/registration';
import { IconFigmaArrowRight, IconFigmaMail, IconFigmaShieldCheck } from '@/assets/icons';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';
import { AuthInfoBox } from '@/features/auth/components/AuthInfoBox';
import { formatRole } from '@/utils/format';

// Backend vraća generički objekt (Map<String, Object>) — nema OpenAPI sheme
// za orgName/role, pa se tipiziramo ručno (isti obrazac kao OrganisationDetail/
// ActionToken drugdje u ovoj bazi koda).
interface VerifyEmailResult {
  orgName: string;
  role: string;
  firstName: string;
  lastName: string;
  email: string;
}

export const VerifyEmailCard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token');
  const [verifiedResult, setVerifiedResult] = useState<VerifyEmailResult | null>(null);

  const verifyEmailMutation = useVerifyEmail({
    mutation: {
      onSuccess: (data) => setVerifiedResult(data as unknown as VerifyEmailResult),
      onError: () => navigate('/verify-email/expired')
    }
  });

  // StrictMode pokreće efekte dvaput u dev okruženju — token je jednokratan
  // pa drugi poziv mora biti spriječen, inače bi ispravan token bio odbijen
  // (već iskorišten) odmah nakon što je prvi poziv uspio.
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (token && !hasStartedRef.current) {
      hasStartedRef.current = true;
      verifyEmailMutation.mutate({ data: { token } });
    }
  }, [token]);

  if (verifiedResult) {
    const orgRows: [string, string][] = [
      [t('verifyEmail.successOrgLabel'), verifiedResult.orgName],
      [t('verifyEmail.successStatusLabel'), t('platformAdmin.statusActive')]
    ];
    const firstUserRows: [string, string][] = [
      [t('verifyEmail.successNameLabel'), `${verifiedResult.firstName} ${verifiedResult.lastName}`],
      [t('verifyEmail.successEmailLabel'), verifiedResult.email],
      [t('verifyEmail.successRoleLabel'), formatRole(verifiedResult.role)]
    ];

    return (
      <AuthCard px={32} py={36} w={{ base: '100%', md: 440 }}>
        <Stack align="center" gap={14}>
          <Center
            w={56}
            h={56}
            style={{
              borderRadius: 12,
              backgroundColor: 'var(--color-accent-secondary-soft)',
              color: 'var(--color-icon-accent)'
            }}
          >
            <IconFigmaShieldCheck size={24} />
          </Center>
          <Title order={2} fz={24} fw={700} ta="center" style={brandTextStyle}>
            {t('verifyEmail.successTitle')}
          </Title>
          <Text size="sm" ta="center" style={{ color: 'var(--color-text-secondary)' }}>
            {t('verifyEmail.successMessage')}
          </Text>

          <AuthInfoBox rows={orgRows} />

          <Text fz={12} fw={600} w="100%" style={{ color: 'var(--color-text-secondary)' }}>
            {t('verifyEmail.successFirstUserLabel')}
          </Text>
          <AuthInfoBox rows={firstUserRows} />

          <Button
            fullWidth
            rightSection={<IconFigmaArrowRight size={15} />}
            onClick={() => navigate('/login')}
          >
            {t('verifyEmail.successLoginButton')}
          </Button>
        </Stack>
      </AuthCard>
    );
  }

  if (token) {
    return (
      <AuthCard p={32} w={{ base: '100%', md: 420 }}>
        <Stack align="center" gap={14}>
          <Loader size="md" />
          <Text size="sm" ta="center" style={{ color: 'var(--color-text-secondary)' }}>
            {t('verifyEmail.verifying')}
          </Text>
        </Stack>
      </AuthCard>
    );
  }

  return (
    <AuthCard p={32} w={{ base: '100%', md: 420 }}>
      <Stack align="center" gap={14}>
        <Center
          w={56}
          h={56}
          style={{
            borderRadius: 12,
            backgroundColor: 'var(--color-accent-secondary-soft)',
            color: 'var(--color-icon-accent)'
          }}
        >
          <IconFigmaMail size={24} />
        </Center>
        <Title order={2} fz={24} fw={700} ta="center" style={brandTextStyle}>
          {t('verifyEmail.title')}
        </Title>
        <Text size="sm" ta="center" style={{ color: 'var(--color-text-secondary)' }}>
          {t('verifyEmail.message')}
        </Text>

        {email && (
          <Group
            gap={8}
            px={14}
            py={6}
            wrap="nowrap"
            style={{
              borderRadius: 999,
              backgroundColor: 'var(--color-bg-surface-2)',
              border: '1px solid var(--color-border-default)'
            }}
          >
            <IconFigmaMail size={13} color="var(--color-border-default)" />
            <Text fz={14} fw={600} style={{ color: 'var(--color-text-primary)' }}>
              {email}
            </Text>
          </Group>
        )}

        <Text fz={10} ta="center" style={{ color: 'var(--color-text-muted)' }}>
          {t('verifyEmail.loginHint')}
        </Text>
      </Stack>
    </AuthCard>
  );
};
